import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable } from '@/components/data/DataTable'
import { useRecoverRequests, useRequestIDOptions, useLocationOptions, useTeamOptions, useRecoverySetOptions, useScenarioOptions, useRecoverRequestStats, useAssignEngineer, useMarkComplete } from '../hooks'
import { createRecoverRequestColumns } from '../columns'
import { AdminRoute } from '@/components/auth/AdminRoute'
import { JsonViewDialog } from '@/components/ui/JsonViewDialog'
import { RunbookViewDialog } from '../components/RunbookViewDialog'
import { RecoverRequestDetailsDialog } from '../components/RecoverRequestDetailsDialog'
import { AssignEngineerDialog } from '../components/AssignEngineerDialog'
import { ScenarioInfoDialog } from '../components/ScenarioInfoDialog'
import { SubObjectProgressDialog } from '../components/SubObjectProgressDialog'
import { MarkCompleteConfirmDialog } from '@/features/my-assignments/components/MarkCompleteConfirmDialog'
import { ObjectQRCodeDialog } from '@/features/objects/components/ObjectQRCodeDialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { X, FileText, Package, MapPin } from 'lucide-react'
import type { RecoverRequest } from '../api'
import { debounce } from '@/lib/utils'

export function RecoverRequestListPage() {
    const { t } = useTranslation()
    const { i18n } = useTranslation()
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(20)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [requestIDFilter, setRequestIDFilter] = useState<string>('')
    const [locationFilter, setLocationFilter] = useState<string>('')
    const [teamFilter, setTeamFilter] = useState<string>('')
    const [recoverySetFilter, setRecoverySetFilter] = useState<string>('')
    const [scenarioFilter, setScenarioFilter] = useState<string>('')
    const [jsonDialog, setJsonDialog] = useState<{ title: string; content: string | null } | null>(null)
    const [runbookDialog, setRunbookDialog] = useState<{ objectID: string; scenario: number | null; forecastStartTime: string | Date | null } | null>(null)
    const [detailsDialog, setDetailsDialog] = useState<{ objectID: string; requestID: string } | null>(null)
    const [assignEngineerDialog, setAssignEngineerDialog] = useState<{ objectID: string; recoverRequestId: number | null; waitGo: string | null; analytics: number | null; responsibleTeamId: number | null; locationId: number | null } | null>(null)
    const [showQRCodeDialog, setShowQRCodeDialog] = useState(false)
    const [qrCodeObjectID, setQrCodeObjectID] = useState<string | null>(null)
    const [subObjectProgressDialog, setSubObjectProgressDialog] = useState<{ mainId: string; objectDescription?: string | null } | null>(null)
    const [scenarioInfoModal, setScenarioInfoModal] = useState<{ requestID: string | null; scenario: number | null; objectID: string | null; objectDescription: string | null } | null>(null)
    const [markCompleteDialog, setMarkCompleteDialog] = useState<{ objectID: string; objectDescription: string | null; recoverRequestId: number | null } | null>(null)
    const markComplete = useMarkComplete()

    const { data: requestIDOptions } = useRequestIDOptions()
    const { data: locationOptions } = useLocationOptions()
    const { data: teamOptions } = useTeamOptions()
    const { data: recoverySetOptions } = useRecoverySetOptions()
    const { data: scenarioOptions } = useScenarioOptions()
    const { data: stats, isLoading: statsLoading } = useRecoverRequestStats()

    // Debounce Object ID search so we don't refetch on every keystroke
    const debouncedSetSearch = useMemo(() => debounce((value: string) => setDebouncedSearch(value), 350), [])

    useEffect(() => {
        debouncedSetSearch(search)
    }, [search, debouncedSetSearch])

    const { data: recoverRequestsResponse } = useRecoverRequests({ 
        page, 
        perPage: limit,
        search: debouncedSearch || undefined,
        requestID: requestIDFilter || undefined,
        location: locationFilter || undefined,
        team: teamFilter || undefined,
        recoverySet: recoverySetFilter || undefined,
        scenario: scenarioFilter || undefined,
    })

    const recoverRequests: RecoverRequest[] = useMemo(() => {
        if (!recoverRequestsResponse) return []
        const dataArray = recoverRequestsResponse.data?.data || recoverRequestsResponse.data
        if (!dataArray || !Array.isArray(dataArray)) {
            return []
        }
        return dataArray
    }, [recoverRequestsResponse])

    const handleShowMetadata = useCallback((title: string, content: string | null) => {
        setJsonDialog({ title, content })
    }, [])

    const handleShowRunbook = useCallback((objectID: string, scenario: number | null, forecastStartTime: string | Date | null) => {
        setRunbookDialog({ objectID, scenario, forecastStartTime })
    }, [])

    const handleShowFloorPlan = useCallback((objectID: string) => {
        // TODO: Implement floor plan view
        console.log('Show floor plan for:', objectID)
    }, [])

    const handleAssignEngineer = useCallback((objectID: string, recoverRequestId: number | null, waitGo: string | null, analytics: number | null, responsibleTeamId: number | null, locationId: number | null) => {
        console.log('🔍 DEBUG handleAssignEngineer:', {
            objectID,
            recoverRequestId,
            waitGo,
            analytics,
            responsibleTeamId,
            locationId,
            responsibleTeamIdType: typeof responsibleTeamId,
            locationIdType: typeof locationId
        })
        setAssignEngineerDialog({ objectID, recoverRequestId, waitGo, analytics, responsibleTeamId, locationId })
    }, [])

    const assignEngineer = useAssignEngineer()

    const handleConfirmAssignEngineer = useCallback((selectedUserId?: string | number | null) => {
        if (!assignEngineerDialog || !selectedUserId) {
            console.warn('Cannot assign engineer: missing dialog data or user ID')
            return
        }

        if (!assignEngineerDialog.recoverRequestId) {
            console.warn('Cannot assign engineer: missing recoverRequestId')
            return
        }

        console.log('🔍 DEBUG handleConfirmAssignEngineer - About to call API:', {
            recoverRequestId: assignEngineerDialog.recoverRequestId,
            recoverRequestIdType: typeof assignEngineerDialog.recoverRequestId,
            selectedUserId,
            selectedUserIdType: typeof selectedUserId,
            assignEngineerDialog
        })

        assignEngineer.mutate(
            {
                requestID: assignEngineerDialog.recoverRequestId,
                userID: selectedUserId
            },
            {
                onSuccess: () => {
                    setAssignEngineerDialog(null)
                },
                onError: (error: any) => {
                    console.error('🔍 DEBUG assignEngineer mutation error:', error)
                }
            }
        )
    }, [assignEngineerDialog, assignEngineer])

    const handleShowObjectDetails = useCallback((objectID: string, requestID: string) => {
        setDetailsDialog({ objectID, requestID })
    }, [])

    const handleShowQRCode = useCallback((objectID: string) => {
        setQrCodeObjectID(objectID)
        setShowQRCodeDialog(true)
    }, [])

    const handleShowSubObjectProgress = useCallback((mainId: string, objectDescription?: string | null) => {
        setSubObjectProgressDialog({ mainId, objectDescription })
    }, [])

    const handleShowScenarioInfo = useCallback((requestID: string | null, scenario: number | null, objectID?: string | null, objectDescription?: string | null) => {
        setScenarioInfoModal({ requestID, scenario, objectID: objectID ?? null, objectDescription: objectDescription ?? null })
    }, [])

    const handleMarkComplete = useCallback((objectID: string, objectDescription: string | null, recoverRequestId: number | null) => {
        setMarkCompleteDialog({ objectID, objectDescription, recoverRequestId })
    }, [])

    const handleConfirmMarkComplete = useCallback(() => {
        if (!markCompleteDialog || markCompleteDialog.recoverRequestId == null) return
        markComplete.mutate(
            { requestID: markCompleteDialog.recoverRequestId },
            { onSuccess: () => setMarkCompleteDialog(null) }
        )
    }, [markCompleteDialog, markComplete])

    const columns = useMemo(
        () => createRecoverRequestColumns(t, i18n.language, handleShowMetadata, handleShowRunbook, handleShowFloorPlan, handleAssignEngineer, handleShowObjectDetails, handleShowQRCode, handleShowSubObjectProgress, handleShowScenarioInfo, handleMarkComplete),
        [t, i18n.language, handleShowMetadata, handleShowRunbook, handleShowFloorPlan, handleAssignEngineer, handleShowObjectDetails, handleShowQRCode, handleShowSubObjectProgress, handleShowScenarioInfo, handleMarkComplete]
    )

    const handleExport = useCallback(() => {
        console.log('Export recover requests to CSV')
    }, [])

    /** Format: <ObjectID>, <RequestDate>, Scenario <ScenarioName>; RequestDate uses current language */
    const formatRequestSetOption = useCallback((option: { objectID: string | null; requestDate: string | Date | null; scenarioName: string | null }) => {
        const { objectID, requestDate, scenarioName } = option
        const parts: string[] = []
        if (objectID) parts.push(objectID)
        if (requestDate) {
            const date = requestDate instanceof Date ? requestDate : new Date(requestDate)
            if (!isNaN(date.getTime())) {
                const formattedDate = new Intl.DateTimeFormat(i18n.language || 'en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                }).format(date)
                parts.push(formattedDate)
            }
        }
        if (scenarioName) parts.push(`Scenario ${scenarioName}`)
        return parts.join(', ') || ''
    }, [i18n.language])

    const handleClearFilters = useCallback(() => {
        setRequestIDFilter('')
        setLocationFilter('')
        setTeamFilter('')
        setRecoverySetFilter('')
        setScenarioFilter('')
        setSearch('')
        setPage(1)
    }, [])

    return (
        <AdminRoute>
            <div className="space-y-[var(--spacing-section)]" style={{ padding: 'var(--spacing-page)' }}>
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl lg:text-2xl font-bold">{t('recoverRequests.title')}</h1>
                        <p className="text-muted-foreground">
                            {t('recoverRequests.description')}
                        </p>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid gap-4 md:grid-cols-3 mb-16">
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.numberRequests')}</p>
                                <p className="text-2xl font-bold">
                                    {statsLoading ? '...' : (stats?.uniqueRequestCount || 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <Package className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.numberObjects')}</p>
                                <p className="text-2xl font-bold">
                                    {statsLoading ? '...' : (stats?.objectCount || 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border bg-card p-6">
                        <div className="flex items-center space-x-2">
                            <MapPin className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium">{t('recoverRequests.numberLocations')}</p>
                                <p className="text-2xl font-bold">
                                    {statsLoading ? '...' : (stats?.locationCount || 0)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                {/* Table - Full Width - Extended */}
                <div
                    className="overflow-x-auto [&_td]:!py-1"
                    style={{
                        marginLeft: 'calc(var(--spacing-page) * -2)',
                        marginRight: 'calc(var(--spacing-page) * -2)',
                        paddingLeft: 'calc(var(--spacing-page) * 0.05)',
                        paddingRight: 'calc(var(--spacing-page) * 0.05)',
                        width: 'calc(100vw - var(--sidebar-width-expanded) - var(--spacing-page) * 0.1)',
                        maxWidth: 'none',
                        minWidth: '1400px',
                    }}
                >
                    <DataTable
                        columns={columns}
                        data={recoverRequests}
                        getCellClassName={(row: RecoverRequest, columnId: string) => (columnId === 'objectID' ? (row.waitGo?.toUpperCase() === 'GO' ? 'border-l-4 border-l-[rgb(151,200,100)]' : 'border-l-4 border-l-transparent') : '')}
                        firstHeaderCellClassName="border-l-4 border-l-[hsl(var(--table-header))]"
                        searchKey="objectID"
                        searchPlaceholder={t('recoverRequests.searchObjectID')}
                        searchValue={search}
                        onSearchChange={(value) => {
                            setSearch(value)
                            setPage(1)
                        }}
                        onExport={handleExport}
                        exportButtonText={t('recoverRequests.export')}
                        enableRowSelection={false}
                        pagination={{
                            page,
                            limit,
                            total: recoverRequestsResponse?.data?.total || 0,
                            onPageChange: setPage,
                            onPageSizeChange: setLimit,
                        }}
                        toolbarContent={
                            <div className="flex items-end gap-2 ml-4 flex-nowrap">
                                {/* Scenario Filter */}
                                <div className="w-[200px] flex-shrink-0 flex flex-col gap-1">
                                    <label className="text-xs font-medium text-muted-foreground">Scenario</label>
                                    <Select value={scenarioFilter || 'all'} onValueChange={(value) => {
                                        setScenarioFilter(value === 'all' ? '' : value)
                                        setPage(1)
                                    }}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('recoverRequests.filterScenario', 'Scenario')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('common.all')}</SelectItem>
                                            {scenarioOptions?.map((opt) => (
                                                <SelectItem key={opt.id} value={String(opt.id)}>
                                                    {opt.name || '-'}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Request ID Filter */}
                                <div className="w-[220px] flex-shrink-0 flex flex-col gap-1">
                                    <label className="text-xs font-medium text-muted-foreground">Request</label>
                                    <Select value={requestIDFilter || 'all'} onValueChange={(value) => {
                                        setRequestIDFilter(value === 'all' ? '' : value)
                                        setPage(1)
                                    }}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('recoverRequests.filterRequestID')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('common.all')}</SelectItem>
                                            {requestIDOptions?.map((option) => (
                                                <SelectItem key={option.requestID || ''} value={option.requestID || ''}>
                                                    {formatRequestSetOption(option)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Location Filter */}
                                <div className="w-[180px] flex-shrink-0 flex flex-col gap-1">
                                    <label className="text-xs font-medium text-muted-foreground">Location</label>
                                    <Select value={locationFilter || 'all'} onValueChange={(value) => {
                                        setLocationFilter(value === 'all' ? '' : value)
                                        setPage(1)
                                    }}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('recoverRequests.filterLocation')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('common.all')}</SelectItem>
                                            {locationOptions?.map((location) => (
                                                <SelectItem key={location} value={location}>
                                                    {location}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Team Filter */}
                                <div className="w-[180px] flex-shrink-0 flex flex-col gap-1">
                                    <label className="text-xs font-medium text-muted-foreground">Team</label>
                                    <Select value={teamFilter || 'all'} onValueChange={(value) => {
                                        setTeamFilter(value === 'all' ? '' : value)
                                        setPage(1)
                                    }}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('recoverRequests.filterTeam')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('common.all')}</SelectItem>
                                            {teamOptions?.map((team) => (
                                                <SelectItem key={team} value={team}>
                                                    {team}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Recovery Set Filter */}
                                <div className="w-[180px] flex-shrink-0 flex flex-col gap-1">
                                    <label className="text-xs font-medium text-muted-foreground">Recovery Set</label>
                                    <Select value={recoverySetFilter || 'all'} onValueChange={(value) => {
                                        setRecoverySetFilter(value === 'all' ? '' : value)
                                        setPage(1)
                                    }}>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('recoverRequests.filterRecoverySet')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">{t('common.all')}</SelectItem>
                                            {recoverySetOptions?.map((set) => (
                                                <SelectItem key={set} value={set}>
                                                    {set}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Clear Filters Button */}
                                {(requestIDFilter || locationFilter || teamFilter || recoverySetFilter || scenarioFilter || search) && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleClearFilters}
                                        className="gap-2 flex-shrink-0"
                                    >
                                        <X className="h-4 w-4" />
                                        {t('recoverRequests.clearFilters')}
                                    </Button>
                                )}
                            </div>
                        }
                    />
                </div>

                {/* JSON View Dialog */}
                {jsonDialog && (
                    <JsonViewDialog
                        open={!!jsonDialog}
                        onOpenChange={(open) => {
                            if (!open) setJsonDialog(null)
                        }}
                        title={jsonDialog.title}
                        content={jsonDialog.content}
                    />
                )}

                {/* Runbook View Dialog */}
                {runbookDialog && (
                    <RunbookViewDialog
                        open={!!runbookDialog}
                        onOpenChange={(open) => {
                            if (!open) setRunbookDialog(null)
                        }}
                        objectID={runbookDialog.objectID}
                        scenario={runbookDialog.scenario}
                        forecastStartTime={runbookDialog.forecastStartTime}
                    />
                )}

                {/* Recover Request Details Dialog */}
                {detailsDialog && (
                    <RecoverRequestDetailsDialog
                        open={!!detailsDialog}
                        onOpenChange={(open) => {
                            if (!open) setDetailsDialog(null)
                        }}
                        objectID={detailsDialog.objectID}
                        requestID={detailsDialog.requestID}
                    />
                )}

                {/* Assign Engineer Dialog */}
                {assignEngineerDialog && (
                    <AssignEngineerDialog
                        open={!!assignEngineerDialog}
                        onOpenChange={(open) => {
                            if (!open) {
                                setAssignEngineerDialog(null)
                            }
                        }}
                        objectID={assignEngineerDialog.objectID}
                        recoverRequestId={assignEngineerDialog.recoverRequestId}
                        waitGo={assignEngineerDialog.waitGo}
                        analytics={assignEngineerDialog.analytics}
                        responsibleTeamId={assignEngineerDialog.responsibleTeamId}
                        locationId={assignEngineerDialog.locationId}
                        onConfirm={handleConfirmAssignEngineer}
                        isLoading={assignEngineer.isPending}
                    />
                )}

                {/* Scenario info modal (RecoveryScenarios + RequestSets) */}
                <ScenarioInfoDialog
                    open={!!scenarioInfoModal}
                    onOpenChange={(open) => {
                        if (!open) setScenarioInfoModal(null)
                    }}
                    requestID={scenarioInfoModal?.requestID ?? null}
                    scenario={scenarioInfoModal?.scenario ?? null}
                    objectID={scenarioInfoModal?.objectID ?? null}
                    objectDescription={scenarioInfoModal?.objectDescription ?? null}
                />

                {/* Sub-object progress dialog */}
                {subObjectProgressDialog && (
                    <SubObjectProgressDialog
                        open={!!subObjectProgressDialog}
                        onOpenChange={(open) => {
                            if (!open) setSubObjectProgressDialog(null)
                        }}
                        mainId={subObjectProgressDialog.mainId}
                        objectDescription={subObjectProgressDialog.objectDescription}
                    />
                )}

                {/* QR Code Dialog */}
                <ObjectQRCodeDialog
                    open={showQRCodeDialog}
                    onOpenChange={(open) => {
                        setShowQRCodeDialog(open)
                        if (!open) setQrCodeObjectID(null)
                    }}
                    objectID={qrCodeObjectID}
                />
            </div>

            {/* Mark Complete Confirm Dialog */}
            {markCompleteDialog && (
                <MarkCompleteConfirmDialog
                    open={!!markCompleteDialog}
                    onOpenChange={(open) => { if (!open) setMarkCompleteDialog(null) }}
                    objectID={markCompleteDialog.objectID}
                    objectDescription={markCompleteDialog.objectDescription}
                    onConfirm={handleConfirmMarkComplete}
                    isLoading={markComplete.isPending}
                />
            )}
        </AdminRoute>
    )
}
